import type {
  IBoatDockAdView,
  IBoatMotorAdView,
  IBoatNewSaleAdView,
  IBoatRentAdView,
  IBoatSaleAdView,
  IBoatWantedAdView,
} from "@finn-no/adview-provider-frontend-types";
import type { BoatAd } from "@mobility-item-page/types";
import { KeyInfo } from "../../key-info/KeyInfo.tsx";
import { Color } from "../Color.tsx";
import { Condition } from "../Condition.tsx";
import { Fuel } from "../Fuel.tsx";
import { LengthCm } from "../LengthCm.tsx";
import { Make } from "../Make.tsx";
import { Model } from "../Model.tsx";
import { ModelYear } from "../ModelYear.tsx";
import { MotorType } from "../MotorType.tsx";
import { NoOfSleepers } from "../NoOfSleepers.tsx";
import { RegistrationNumber } from "../RegistrationNumber.tsx";
import { SpecificationsArea } from "../SpecificationsArea.tsx";
import { TradeType } from "../TradeType.tsx";
import { Weight } from "../Weight.tsx";
import { BoatEngine } from "./BoatEngine.tsx";
import { BoatLocation } from "./BoatLocation.tsx";
import { BoatSeats } from "./BoatSeats.tsx";
import { BoatType } from "./BoatType.tsx";
import { Depth } from "./Depth.tsx";
import { DockLocation } from "./DockLocation.tsx";
import { LengthFeet } from "./LengthFeet.tsx";
import { Material } from "./Material.tsx";
import { MaxSpeed } from "./MaxSpeed.tsx";
import { MotorIncluded } from "./MotorIncluded.tsx";
import { MotorMake } from "./MotorMake.tsx";
import { Width } from "./Width.tsx";

export const BoatSpecifications = ({ ad }: { ad: BoatAd }) => {
  const specifications = () => {
    switch (ad.adViewType) {
      case "boat-sale":
      case "boat-new-sale":
        return <BoatSaleSpecifications ad={ad} />;
      case "boat-rent":
        return <BoatRentSpecifications ad={ad} />;
      case "boat-wanted":
        return <BoatWantedSpecifications ad={ad} />;
      case "boat-motor":
        return <BoatMotorSpecifications ad={ad} />;
      case "boat-dock":
        return <BoatDockSpecifications ad={ad} />;
      default:
        return null;
    }
  };

  const content = specifications();

  if (!content) {
    return null;
  }

  return (
    <SpecificationsArea>
      <KeyInfo>{content}</KeyInfo>
    </SpecificationsArea>
  );
};

const BoatSaleSpecifications = ({ ad }: { ad: IBoatSaleAdView | IBoatNewSaleAdView }) => {
  return (
    <>
      {/*General information*/}
      <Condition condition={ad.condition} />
      <Make make={ad.make} />
      <Model model={ad.model} />
      <ModelYear year={ad.year} />
      <BoatType boatClass={ad.boat_class} />

      {/*Product properties*/}
      <Fuel fuel={ad.motor?.fuel} />

      <MotorIncluded motorIncluded={ad.motor_included} />
      <BoatEngine horsepower={ad.motor?.size} />
      <MotorMake make={ad.motor?.make} />
      <MotorType motorType={ad.motor?.motor_type} />
      <MaxSpeed maxSpeed={ad.max_speed} />

      <Material material={ad.material} />
      <Weight weight={ad.weight} />
      <LengthFeet length={ad.length} />
      <LengthCm lengthCm={ad.length_cm} />
      <Depth depth={ad.depth} />
      <Width width={ad.width} />
      <BoatSeats noOfSeats={ad.no_of_seats} />
      <NoOfSleepers noOfSleepers={ad.no_of_sleepers} />

      {/*Appearance*/}
      <Color color={ad.color} />

      {/*Registration, warranty, owners, etc*/}
      <BoatLocation boatLocation={ad.boat_location} />
      <RegistrationNumber registrationNumber={ad.registration_number} />
    </>
  );
};

const BoatRentSpecifications = ({ ad }: { ad: IBoatRentAdView }) => {
  return (
    <>
      {/*General information*/}
      <Make make={ad.make} />
      <Model model={ad.model} />
      <ModelYear year={ad.year} />
      <BoatType boatClass={ad.boat_class} />

      {/*Product properties*/}
      <Fuel fuel={ad.motor?.fuel} />

      <BoatEngine horsepower={ad.motor?.size} />
      <MotorMake make={ad.motor?.make} />
      <MotorType motorType={ad.motor?.motor_type} />

      <Material material={ad.material} />
      <Weight weight={ad.weight} />
      <LengthFeet length={ad.length} />
      <LengthCm lengthCm={ad.length_cm} />
      <Depth depth={ad.depth} />
      <Width width={ad.width} />
      <BoatSeats noOfSeats={ad.no_of_seats} />
      <NoOfSleepers noOfSleepers={ad.no_of_sleepers} />

      {/*Appearance*/}
      <Color color={ad.color} />

      {/*Registration, warranty, owners, etc*/}
      <RegistrationNumber registrationNumber={ad.registration_number} />
    </>
  );
};

const BoatWantedSpecifications = ({ ad }: { ad: IBoatWantedAdView }) => {
  return (
    <>
      {/*General information*/}
      <BoatType boatClass={ad.boat_class} />
    </>
  );
};

const BoatDockSpecifications = ({ ad }: { ad: IBoatDockAdView }) => {
  return (
    <>
      {/*Product properties*/}
      <Width width={ad.width} />

      {/*Registration, warranty, owners, etc*/}
      <DockLocation situation={ad.situation} />
      <TradeType tradeType={ad.trade_type} />
    </>
  );
};

const BoatMotorSpecifications = ({ ad }: { ad: IBoatMotorAdView }) => {
  return (
    <>
      {/*General information*/}
      <Make make={ad.make} />

      {/*Product properties*/}
      <MotorType motorType={ad.motor_type} />
      <BoatEngine horsepower={ad.effect} />
    </>
  );
};
